import { useState, type FormEvent } from "react";
import { Aviso, Botao, Campo, CampoLongo, Cartao } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import { nomeDaOrganizacao, useOrganizacoesDoCadastro } from "../../nucleo/cadastro";
import {
  ATIVO_EM_BRANCO,
  CATEGORIAS_DE_ATIVO,
  ROTULO_DA_CATEGORIA,
  ROTULO_DO_STATUS,
  STATUS_DE_ATIVO,
  corpoDoAtivo,
  formularioDoAtivo,
  useSalvarAtivo,
  validarAtivo,
  type Ativo,
  type FormularioDeAtivo as Formulario,
} from "../../nucleo/ativos";

/** Cadastro e edição de ativo — lado da equipe (inclui custo e observação interna). */
export function FormularioDeAtivo({
  ativo,
  organizacaoInicial,
  aoSalvar,
  aoCancelar,
}: {
  ativo?: Ativo;
  organizacaoInicial?: string;
  aoSalvar: (id: string) => void;
  aoCancelar: () => void;
}) {
  const novo = ativo === undefined;
  const organizacoes = useOrganizacoesDoCadastro();
  const salvar = useSalvarAtivo();
  const [form, setForm] = useState<Formulario>(
    ativo ? formularioDoAtivo(ativo) : { ...ATIVO_EM_BRANCO, organizacao_id: organizacaoInicial ?? "" },
  );
  const [problema, setProblema] = useState<string | null>(null);

  function atualizar<K extends keyof Formulario>(campo: K, valor: Formulario[K]) {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const erro = validarAtivo(form, novo);
    setProblema(erro);
    if (erro) return;
    salvar.mutate(
      { id: ativo?.id, corpo: corpoDoAtivo(form, { novo, incluirInterno: true }) },
      { onSuccess: (resposta) => aoSalvar(resposta.id) },
    );
  }

  return (
    <Cartao titulo={novo ? "Novo ativo" : `Editar ${ativo.nome}`} data-testid="formulario-de-ativo">
      <form onSubmit={enviar} noValidate>
        {novo ? (
          <div className="campo">
            <label className="campo__rotulo" htmlFor="organizacao-do-ativo">
              Organização
              <span className="campo__obrigatorio" aria-hidden="true">
                *
              </span>
            </label>
            <select
              id="organizacao-do-ativo"
              className="campo__controle"
              value={form.organizacao_id}
              onChange={(e) => atualizar("organizacao_id", e.target.value)}
            >
              <option value="">Escolha a organização</option>
              {(organizacoes.data ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {nomeDaOrganizacao(item)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="grid gap-3 md:grid-cols-3">
          <Campo rotulo="Nome" obrigatorio value={form.nome} onChange={(e) => atualizar("nome", e.target.value)} />
          <div className="campo">
            <label className="campo__rotulo" htmlFor="categoria-do-ativo">
              Categoria
            </label>
            <select
              id="categoria-do-ativo"
              className="campo__controle"
              value={form.categoria}
              onChange={(e) => atualizar("categoria", e.target.value as Formulario["categoria"])}
            >
              {CATEGORIAS_DE_ATIVO.map((item) => (
                <option key={item} value={item}>
                  {ROTULO_DA_CATEGORIA[item]}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label className="campo__rotulo" htmlFor="status-do-ativo">
              Status
            </label>
            <select
              id="status-do-ativo"
              className="campo__controle"
              value={form.status}
              onChange={(e) => atualizar("status", e.target.value as Formulario["status"])}
            >
              {STATUS_DE_ATIVO.map((item) => (
                <option key={item} value={item}>
                  {ROTULO_DO_STATUS[item]}
                </option>
              ))}
            </select>
          </div>
          <Campo rotulo="Fabricante" value={form.fabricante} onChange={(e) => atualizar("fabricante", e.target.value)} />
          <Campo rotulo="Modelo" value={form.modelo} onChange={(e) => atualizar("modelo", e.target.value)} />
          <Campo
            rotulo="Número de série"
            value={form.numero_serie}
            onChange={(e) => atualizar("numero_serie", e.target.value)}
          />
          <Campo
            rotulo="Data da compra"
            type="date"
            value={form.data_compra}
            onChange={(e) => atualizar("data_compra", e.target.value)}
          />
          <Campo
            rotulo="Garantia até"
            type="date"
            value={form.garantia_ate}
            onChange={(e) => atualizar("garantia_ate", e.target.value)}
          />
          <Campo
            rotulo="Localização"
            value={form.localizacao}
            onChange={(e) => atualizar("localizacao", e.target.value)}
          />
        </div>
        <CampoLongo
          rotulo="Descrição (o cliente vê)"
          value={form.descricao}
          onChange={(e) => atualizar("descricao", e.target.value)}
        />
        <h3 className="titulo-secao">Só a equipe vê</h3>
        <div className="grid gap-3 md:grid-cols-[1fr_2fr]">
          <Campo
            rotulo="Custo (R$)"
            inputMode="decimal"
            value={form.custo}
            onChange={(e) => atualizar("custo", e.target.value)}
          />
          <Campo
            rotulo="Observação interna"
            value={form.observacao_interna}
            onChange={(e) => atualizar("observacao_interna", e.target.value)}
          />
        </div>
        {problema ? <Aviso tom="erro" className="mb-3">{problema}</Aviso> : null}
        {salvar.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(salvar.error)}</Aviso> : null}
        <div className="flex gap-2">
          <Botao type="submit" disabled={salvar.isPending} data-testid="salvar-ativo">
            {salvar.isPending ? "Salvando…" : "Salvar ativo"}
          </Botao>
          <Botao variante="fantasma" onClick={aoCancelar}>
            Cancelar
          </Botao>
        </div>
      </form>
    </Cartao>
  );
}
